import { useTipTapEditorContext } from "@circle-react-uikit/TipTap/TipTapEditorContext";
import { Typography } from "@circle-react-uikit/Typography";
import {
  checkMessageApiAccess,
  hasMessageAccess,
} from "@circle-react/components/shared/uikit/TipTap/Extensions/CustomExtensions/ChatMessageLink/helper";
import { useCurrentCommunityMember } from "@circle-react/contexts/punditUserContext";
import { dateStringToTimeAgo } from "@circle-react/helpers/dateTimeHelpers/timeAgo";
import { useEnterOrSpaceKeyDownHandler } from "@circle-react/hooks/useEnterOrSpaceKeyDownHandler";
import { useBoolean } from "@circle-react/hooks/utils/useBoolean";
import type { Sender } from "@circle-react/types/Chat/ChatRoomMessage";

import { t } from "@/i18n-js/instance";
import { ConfirmationModal } from "@/react/components/SettingsApp/CompassCompat/ConfirmationModal";

import { MessageItemCompassButton } from "./MessageItemCompassButton";

interface MessageItemProps {
  message: {
    id: number;
    sender: Sender;
    data: {
      url: string;
      chat_room_uuid: string;
      message_body: string;
      sent_at: string;
      attachments_count: number;
    };
  };
}

export const MessageItem = ({ message }: MessageItemProps) => {
  const currentCommunityMember = useCurrentCommunityMember();
  const [shouldShowAlert, toggleShouldShowAlert] = useBoolean();
  const { chatRoomAccessArr = [], editor } = useTipTapEditorContext() || {};
  const {
    sender,
    data: {
      url,
      chat_room_uuid: chatRoomUuid,
      message_body: messageBody,
      sent_at: sentAt,
      attachments_count: attachmentsCount = 0,
    },
    sender: { community_member_id: senderCommunityMemberId },
    id,
  } = message;
  const timeAgo = dateStringToTimeAgo(sentAt);

  const redirectToLink = async () => {
    const hasAccessArray = chatRoomAccessArr.length > 0;
    const shouldCheckApiAccess = !editor.isEditable && !hasAccessArray;
    const isMessageOfCurrentCommunityMember =
      senderCommunityMemberId === currentCommunityMember?.id;

    const checkMessageAccess = () =>
      editor.isEditable
        ? true // Message yet to be created
        : hasMessageAccess(id, chatRoomAccessArr);

    const checkApiAccess = () =>
      checkMessageApiAccess({
        chatRoomUuid,
        messageId: id,
      });

    const hasAccess = isMessageOfCurrentCommunityMember
      ? true
      : shouldCheckApiAccess
        ? await checkApiAccess()
        : checkMessageAccess();

    if (hasAccess) {
      window.open(url, "_blank", "noreferrer");
    } else {
      toggleShouldShowAlert();
    }
  };

  const onKeyDownHandler = useEnterOrSpaceKeyDownHandler(
    () => void redirectToLink(),
  );

  if (!editor) return null;
  return (
    <>
      <MessageItemCompassButton
        redirectToLink={redirectToLink}
        onKeyDownHandler={onKeyDownHandler}
        editor={editor}
        sender={sender}
        messageBody={messageBody}
        attachmentsCount={attachmentsCount}
        timeAgo={timeAgo}
      />
      <ConfirmationModal
        isOpen={shouldShowAlert}
        title={t("messaging.actions.unable_to_open_link.title")}
        onConfirm={toggleShouldShowAlert}
        confirmVariant="danger"
        confirmText={t("close")}
      >
        <Typography.BodyMd>
          {t("messaging.actions.unable_to_open_link.description")}
        </Typography.BodyMd>
      </ConfirmationModal>
    </>
  );
};
